import { create } from 'zustand';
import type { JuniAudience } from '@/modules/juni/juni-api';

/**
 * The conversations, one for the public site and one for the dashboard, kept in memory only.
 *
 * Nothing about a chat is stored anywhere: not on the server, and not in the browser beyond the tab.
 * A store rather than component state so the conversation survives moving between pages: the public
 * pages share no layout, so the widget is remounted on each of them.
 */

export interface JuniMessage {
  id: string;
  role: 'user' | 'assistant';
  /** The question, or the answer so far. */
  text: string;
  /** Juni is busy or cannot answer right now, and says what to do instead. */
  notice: string | null;
  /** The answer stopped, or the question could not be sent. */
  error: string | null;
  pending: boolean;
  startedAt: number;
}

interface JuniState {
  open: Record<JuniAudience, boolean>;
  messages: Record<JuniAudience, JuniMessage[]>;
  /** Whose dashboard conversation this is, so a different sign-in in the same tab starts afresh. */
  customerOwner: string | null;

  setOpen: (audience: JuniAudience, open: boolean) => void;
  add: (audience: JuniAudience, message: JuniMessage) => void;
  update: (audience: JuniAudience, id: string, change: (message: JuniMessage) => Partial<JuniMessage>) => void;
  clear: (audience: JuniAudience) => void;
  claimCustomer: (userId: string | null) => void;
}

export const useJuniStore = create<JuniState>()((set) => ({
  open: { visitor: false, customer: false },
  messages: { visitor: [], customer: [] },
  customerOwner: null,

  setOpen: (audience, open) => set((state) => ({ open: { ...state.open, [audience]: open } })),

  add: (audience, message) =>
    set((state) => ({ messages: { ...state.messages, [audience]: [...state.messages[audience], message] } })),

  update: (audience, id, change) =>
    set((state) => ({
      messages: {
        ...state.messages,
        [audience]: state.messages[audience].map((message) =>
          message.id === id ? { ...message, ...change(message) } : message,
        ),
      },
    })),

  clear: (audience) => set((state) => ({ messages: { ...state.messages, [audience]: [] } })),

  claimCustomer: (userId) =>
    set((state) =>
      state.customerOwner === userId
        ? state
        : { customerOwner: userId, messages: { ...state.messages, customer: [] }, open: { ...state.open, customer: false } },
    ),
}));
